import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import { Sex, feetInchesToCm, clampHeight, cmToFeetInches } from "@/utils/units";
import { CdfJson, interpPercentile, makeDemoCdf } from "@/utils/cdf";
import BellCurveSvg from "@/components/charts/BellCurveSvg";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import NotFound from "./NotFound";
import AdSlot from "@/components/ads/AdSlot";

const defaultCdfs: Record<Sex, CdfJson> = {
  male: makeDemoCdf(175, 7.5),
  female: makeDemoCdf(162, 6.5),
  combined: makeDemoCdf(169, 7.5),
};

export default function HeightImperialPage() {
  const { sex, feetFt, inchIn } = useParams();

  const validSex: Sex | null = sex === "male" || sex === "female" ? (sex as Sex) : null;
  const feet = Number(feetFt);
  const inches = Number(inchIn);

  const isValid = validSex && Number.isFinite(feet) && Number.isFinite(inches) && feet >= 3 && feet <= 8 && inches >= 0 && inches < 12;

  if (!isValid) return <NotFound />;

  const cmRaw = feetInchesToCm(feet, inches);
  const cm = clampHeight(cmRaw);
  const cdf = defaultCdfs[validSex!];
  const percentile = interpPercentile(cdf, cm);
  const mean = cdf.meta.mean_cm;
  const median = cdf.meta.median_cm;

  const title = `${feet} ft ${inches} in ${validSex === "male" ? "Male" : "Female"} Height Percentile (US)`;
  const description = `What percentile is ${feet} ft ${inches} in ${validSex} in the US? Instant answer with mean, median, and chart.`;

  const prevCm = clampHeight(feetInchesToCm(feet, Math.max(0, inches - 1)));
  const nextCm = clampHeight(feetInchesToCm(feet, Math.min(11, inches + 1)));
  const prevFi = cmToFeetInches(prevCm);
  const nextFi = cmToFeetInches(nextCm);

  const calcUrl = useMemo(() => `/?h=${Math.round(cm)}&u=ftin&sex=${validSex}&band=all`, [cm, validSex]);

  return (
    <main className="container mx-auto py-10 space-y-6">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={window.location.origin + window.location.pathname} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin + window.location.pathname} />
        <meta property="og:image" content={window.location.origin + "/og-default.jpg"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={window.location.origin + "/og-default.jpg"} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
            { "@type": "ListItem", position: 2, name: title }
          ]
        })}</script>
      </Helmet>

      <AdSlot slot="banner_top" className="mb-4" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <h1>{title}</h1>

          <Card>
            <CardHeader>
              <CardTitle>Quick answer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p>
                A {feet} ft {inches} in {validSex} is at the <strong>{percentile.toFixed(1)}%</strong> percentile among U.S. adults in the same sex group.
              </p>
              <p className="text-sm text-muted-foreground">
                Mean: {Math.round(mean)} cm • Median: {Math.round(median)} cm • Your entry: {Math.round(cm)} cm
              </p>
              <div className="mt-2">
                <BellCurveSvg mean={mean} std={cdf.meta.std_cm || 7} value={cm} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button asChild>
                  <Link to={calcUrl}>Open in Calculator</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/${validSex}/${prevFi.ft}ft-${Math.round(prevFi.inch)}in-percentile`} aria-label="Previous height">
                    −1 in ({prevFi.ft} ft {Math.round(prevFi.inch)} in)
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/${validSex}/${nextFi.ft}ft-${Math.round(nextFi.inch)}in-percentile`} aria-label="Next height">
                    +1 in ({nextFi.ft} ft {Math.round(nextFi.inch)} in)
                  </Link>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Note: Inputs outside 135–210 cm are softly clamped for display purposes.
              </p>
            </CardContent>
          </Card>

          <AdSlot slot="incontent_1" className="mt-4" />
        </div>
        <aside className="hidden lg:block">
          <AdSlot slot="sidebar_1" />
        </aside>
      </div>
    </main>
  );
}
